// Duplicate Conversion-Action Names — Conversions section, deterministic check.
// Surfaces ENABLED conversion actions whose names collide (case-insensitive, trimmed). For
// each duplicate group, shows the offending members so the auditor can decide which to rename,
// merge, or retire. Multi-primary groups are called out at the top — that's the worst case
// because bidding sees a split signal under the same label.

import type { EvidenceProps } from './registry';

interface ActionRow {
  id?: string;
  name?: string;
  category?: string;
  primaryForGoal: boolean;
  includeInConversionsMetric: boolean;
  last30dConversions: number;
}

interface DuplicateGroup {
  normalizedName: string;
  displayName: string;
  count: number;
  totalLast30dConversions: number;
  hasMultiplePrimary: boolean;
  hasAnyInMetric: boolean;
  members: ActionRow[];
}

interface DuplicateConversionShape {
  totalEnabled: number;
  duplicateGroupCount: number;
  highVolumeThreshold: number;
  groups: DuplicateGroup[];
}

const CATEGORY_LABEL: Record<string, string> = {
  PURCHASE: 'Purchase',
  SIGNUP: 'Sign-up',
  LEAD: 'Lead',
  ADD_TO_CART: 'Add to Cart',
  BEGIN_CHECKOUT: 'Begin Checkout',
  PAGE_VIEW: 'Page View',
  CONTACT: 'Contact',
  PHONE_CALL_LEAD: 'Call'
};

function categoryLabel(c: string | undefined): string {
  if (!c) return '—';
  return CATEGORY_LABEL[c] ?? c;
}

function formatVolume(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString();
}

export const DuplicateConversionEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as DuplicateConversionShape;
  const hasDupes = e.duplicateGroupCount > 0;

  if (!hasDupes) {
    return (
      <div className='evidence'>
        <p className='evidence__lead'>
          All {e.totalEnabled} enabled conversion actions have unique names. No duplicates found.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        <strong>{e.duplicateGroupCount}</strong> duplicate name group
        {e.duplicateGroupCount === 1 ? '' : 's'} found across {e.totalEnabled} enabled conversion
        actions. Duplicate names cause ambiguity in reports and, when both members are counted in
        &ldquo;Conversions&rdquo;, split the bidding signal under the same label.
      </p>

      {e.groups.map((g, i) => (
        <DuplicateGroupCard key={g.normalizedName} group={g} index={i} />
      ))}
    </div>
  );
};

const DuplicateGroupCard = ({ group, index }: { group: DuplicateGroup; index: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      <span className='evidence__group-name'>&ldquo;{group.displayName}&rdquo;</span>
      <span className='evidence__section-tag evidence__section-tag--context'>
        {group.count} actions · {formatVolume(group.totalLast30dConversions)} total/30d
      </span>
      {group.hasMultiplePrimary && (
        <span className='evidence__section-tag evidence__section-tag--actionable'>
          Multiple primary
        </span>
      )}
    </h4>
    <table className='evidence__table evidence__table--duplicate-conversion'>
      <thead>
        <tr>
          <th>Conversion action</th>
          <th>Category</th>
          <th>Role</th>
          <th>Include in &ldquo;Conversions&rdquo;</th>
          <th className='evidence__th--right'>Conv (30d)</th>
        </tr>
      </thead>
      <tbody>
        {group.members.map(m => (
          <tr key={m.id ?? `${m.name}-${index}`}>
            <td>
              <div>{m.name ?? '(unnamed)'}</div>
              {m.id && <div className='evidence__action-id'>{m.id}</div>}
            </td>
            <td>{categoryLabel(m.category)}</td>
            <td>
              <span
                className={`evidence__pill ${
                  m.primaryForGoal ? 'evidence__pill--primary' : 'evidence__pill--secondary'
                }`}
              >
                {m.primaryForGoal ? 'Primary' : 'Secondary'}
              </span>
            </td>
            <td>
              <span
                className={`evidence__pill ${
                  m.includeInConversionsMetric
                    ? 'evidence__pill--in-metric'
                    : 'evidence__pill--excluded'
                }`}
              >
                {m.includeInConversionsMetric ? 'Counted' : 'Excluded'}
              </span>
            </td>
            <td className='evidence__td--right'>{formatVolume(m.last30dConversions)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);
